/**
 * TextService.ts
 * 
 * 职责：
 * 1. 管理文本元素的创建、编辑和删除
 * 2. 处理文本绘制和渲染
 * 3. 提供文本选择功能
 */

import { Point, TextData } from '../../types';
import { ViewportService } from './ViewportService';
import { SelectionService } from './SelectionService';

export class TextService {
  // 存储所有文本元素的数组
  private texts: TextData[] = [];
  
  // Canvas上下文，用于文本渲染和测量
  private ctx: CanvasRenderingContext2D | null = null;
  
  // 视口服务引用，用于坐标转换和触发重新渲染
  private viewportService: ViewportService | null = null;
  
  // 当前文本设置
  private selectedColor: string = '#000000';
  private fontSize: number = 16;
  
  // 编辑状态
  private editingIndex: number = -1;
  public isEditing: boolean = false;
  
  // 当前编辑器元素
  private editorElement: HTMLTextAreaElement | null = null;
  
  // 边框元素引用
  private borderElement: HTMLDivElement | null = null;
  
  // 存储外部点击事件处理函数的引用
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;
  
  // 用于触发画布重绘的回调函数
  private onRenderCallback: (() => void) | null = null;
  
  constructor() {
    // 确保在页面卸载时清理资源
    window.addEventListener('beforeunload', this.cleanup.bind(this));
    
    // 添加事件监听器，在滚动动画的每一帧更新编辑元素位置
    document.addEventListener('viewport-scroll-update', () => {
      if (this.isEditing && this.editingIndex !== -1) {
        this.updateEditingElementsPosition();
      }
    });
    
    // 添加视口变化事件监听器（包括缩放变化）
    document.addEventListener('viewport-changed', () => {
      if (this.isEditing && this.editingIndex !== -1) {
        this.updateEditingElementsPosition();
      }
    });
  }
  
  /**
   * 设置Canvas上下文
   */
  public setContext(ctx: CanvasRenderingContext2D): void {
    this.ctx = ctx;
  }
  
  /**
   * 设置视口服务
   */
  public setViewportService(viewportService: ViewportService): void {
    this.viewportService = viewportService;
  }
  
  /**
   * 设置文本数组
   */
  public setTexts(texts: TextData[]): void {
    this.texts = texts;
  }
  
  /**
   * 获取文本数组
   */
  public getTexts(): TextData[] {
    return this.texts;
  }
  
  /**
   * 设置文本颜色
   */
  public setColor(color: string): void {
    this.selectedColor = color;
  }
  
  /**
   * 获取当前文本颜色
   */
  public getColor(): string {
    return this.selectedColor;
  }
  
  /**
   * 设置字体大小
   */
  public setFontSize(size: number): void {
    this.fontSize = size;
  }
  
  /**
   * 添加新文本
   */
  public addText(x: number, y: number): TextData | null {
    if (!this.ctx || !this.viewportService) {
      return null;
    }
    
    // 如果已经在编辑文本，先停止编辑
    if (this.isEditing) {
      this.stopEditing();
    }
    
    // 获取创建时的缩放比例
    const creationScale = this.viewportService.getScale();
    
    const newText: TextData = {
      text: "",
      baseX: x,
      baseY: y,
      color: this.selectedColor,
      fontSize: this.fontSize,
      isEditing: true,
      creationScale: creationScale // 保存创建时的缩放比例
    };
    
    this.texts.push(newText);
    this.startEditing(this.texts.length - 1);
    
    return newText;
  }
  
  /**
   * 开始编辑文本
   */
  public editText(index: number, clickX?: number, clickY?: number): void {
    if (index < 0 || index >= this.texts.length) return;
    
    if (this.isEditing) {
      this.stopEditing();
    }
    
    this.startEditing(index, clickX, clickY);
  }
  
  /**
   * 开始编辑过程
   */
  private startEditing(index: number, clickX?: number, clickY?: number): void {
    if (!this.ctx || !this.viewportService || index < 0 || index >= this.texts.length) {
      return;
    }
    
    const textData = this.texts[index];
    textData.isEditing = true;
    this.editingIndex = index;
    this.isEditing = true;
    
    // 创建文本编辑器
    this.createEditor(textData, clickX, clickY);
    
    // 通知应用进入编辑模式
    document.dispatchEvent(new CustomEvent('text-edit-start'));
    
    // 不再强制禁用画布缩放功能，保留用户设置的缩放状态
    // if (this.viewportService) {
    //   this.viewportService.setZoomDisabled(true);
    // }
  }
  
  /**
   * 创建文本编辑器
   */
  private createEditor(textData: TextData, clickX?: number, clickY?: number): void {
    if (!this.ctx || !this.viewportService) return;
    
    // 清除可能存在的编辑器
    this.removeEditor();
    
    // 获取当前缩放比例
    const currentScale = this.viewportService.getScale();
    // 获取文本创建时的缩放比例
    const creationScale = textData.creationScale || 1;
    // 计算相对缩放比例（创建时相对于当前的比例）
    const relativeScale = creationScale / currentScale;
    
    // 创建文本区域元素
    const textarea = document.createElement('textarea');
    textarea.id = 'text-editor';
    textarea.value = textData.text;
    textarea.style.position = 'absolute';
    textarea.style.background = 'transparent';
    textarea.style.border = 'none';
    textarea.style.outline = 'none';
    textarea.style.overflow = 'hidden';
    textarea.style.resize = 'none';
    textarea.style.color = textData.color;
    textarea.style.fontFamily = 'Arial, sans-serif';
    
    // 使用考虑缩放比例后的字体大小
    const displayFontSize = textData.fontSize / relativeScale;
    textarea.style.fontSize = `${displayFontSize}px`;
    
    textarea.style.lineHeight = '1.2';
    textarea.style.margin = '0';
    textarea.style.padding = '0';
    textarea.style.textAlign = 'center';
    
    // 转换canvas坐标到屏幕坐标
    const screenPos = this.viewportService.canvasToClient(textData.baseX, textData.baseY);
    
    // 初始位置
    textarea.style.left = `${screenPos.x}px`;
    textarea.style.top = `${screenPos.y - (displayFontSize)/2}px`;
    
    // 先添加到文档以便能够计算尺寸
    document.body.appendChild(textarea);
    
    // 保存当前上下文状态
    if (this.ctx) {
      const ctx = this.ctx;
      ctx.save();
      // 设置字体大小，与drawAllTexts方法中的设置一致
      ctx.font = `${textData.fontSize * (1 / creationScale)}px Arial, sans-serif`;
      
      // 计算文本显示尺寸
      let maxWidth = 0;
      const lines = textData.text.split('\n');
      for (const line of lines) {
        const metrics = ctx.measureText(line);
        maxWidth = Math.max(maxWidth, metrics.width);
      }
      ctx.restore();
      
      // 将Canvas中的文本尺寸转换为屏幕上的物理尺寸
      const displayWidth = maxWidth * currentScale;
      const lineHeight = textData.fontSize * (1 / creationScale) * 1.2;
      const textHeight = Math.max(lineHeight, lines.length * lineHeight);
      const displayHeight = textHeight * currentScale;
      
      // 设置文本域的大小，加上少量额外空间
      const extraSpace = 20 * currentScale;
      if (maxWidth > 0) {
        textarea.style.width = `${displayWidth + extraSpace}px`;
        textarea.style.height = `${displayHeight + 10 * currentScale}px`;
      } else {
        // 空文本时使用默认最小宽度
        const minWidth = textData.fontSize * 5 * currentScale;
        textarea.style.width = `${minWidth}px`;
        textarea.style.height = `${lineHeight * currentScale}px`;
      }
      
      // 确保编辑器居中于文本位置
      textarea.style.left = `${screenPos.x - parseInt(textarea.style.width) / 2}px`;
    }
    
    this.editorElement = textarea;
    
    // 创建文本边框元素，显示文本对象框
    const textBorder = document.createElement('div');
    textBorder.id = 'text-border';
    textBorder.style.position = 'absolute';
    textBorder.style.pointerEvents = 'none'; // 不干扰鼠标事件
    textBorder.style.boxSizing = 'border-box';
    textBorder.style.zIndex = '-1'; // 确保在文本框下方
    
    // 获取当前缩放比例，用于调整边框样式 - 与SelectionService完全一致
    const lineWidthFactor = Math.max(0.5, 1.0 / currentScale);
    const opacityFactor = Math.min(1.0, 1.2 - (currentScale * 0.2)); // 缩放小时增加不透明度
    
    // 设置完全与SelectionService一致的边框样式
    // 注意：我们需要确保边框宽度与Canvas绘制的完全一致
    // 在SelectionService中，线条宽度是直接设置ctx.lineWidth = lineWidthFactor
    // CSS中需要固定为2px才能与Canvas中看起来一致，因为Canvas渲染与CSS渲染有差异
    textBorder.style.border = `2px solid rgba(60, 60, 60, ${Math.min(0.9, Math.max(0.6, opacityFactor))})`;
    
    // 不使用textarea的边界，改用文本的实际渲染边界
    // 保存上下文
    this.ctx!.save();
    
    // 设置字体，确保与绘制和SelectionService一致
    this.ctx!.font = `${textData.fontSize * (1 / creationScale)}px Arial, sans-serif`;
    
    // 计算文本的宽度和高度
    let textWidth = 0;
    const lines = textData.text.split('\n');
    for (const line of lines) {
      const metrics = this.ctx!.measureText(line);
      textWidth = Math.max(textWidth, metrics.width);
    }
    
    // 计算总高度
    const lineHeight = textData.fontSize * (1 / creationScale) * 1.2;
    const textHeight = lines.length * lineHeight - (lines.length > 1 ? lineHeight * 0.2 : 0);
    
    // 计算边界框 - 与SelectionService中的getTextBoundingBox一致
    const halfWidth = textWidth / 2;
    
    // 基础边界（不包含边距）
    const bounds = {
      minX: textData.baseX - halfWidth,
      minY: textData.baseY - (textData.fontSize * (1 / creationScale) / 2), // 从第一行文字的中心点向上偏移半个字体大小
      maxX: textData.baseX + halfWidth,
      maxY: textData.baseY + textHeight - (textData.fontSize * (1 / creationScale) / 2) // 向下延伸整个文本高度，减去半个字体大小
    };
    
    // 添加边距 - 与SelectionService一致
    const margin = 5 / currentScale;
    bounds.minX -= margin;
    bounds.minY -= margin;
    bounds.maxX += margin;
    bounds.maxY += margin;
    
    // 恢复上下文
    this.ctx!.restore();
    
    // 转换为屏幕坐标
    const minPos = this.viewportService.canvasToClient(bounds.minX, bounds.minY);
    const maxPos = this.viewportService.canvasToClient(bounds.maxX, bounds.maxY);
    
    // 设置边框位置和大小
    textBorder.style.left = `${minPos.x}px`;
    textBorder.style.top = `${minPos.y}px`;
    textBorder.style.width = `${maxPos.x - minPos.x}px`;
    textBorder.style.height = `${maxPos.y - minPos.y}px`;
    
    // 添加到文档
    document.body.appendChild(textBorder);
    
    // 保存边框元素引用
    this.borderElement = textBorder;
    
    // 自动调整文本框和边框大小
    const updateBorderSize = () => {
      if (!this.borderElement || !this.editorElement || !this.viewportService) return;
      
      // 保存当前文本值并更新边界
      if (this.editingIndex >= 0 && this.editorElement) {
        // 保存上下文
        this.ctx!.save();
        
        // 获取更新后的文本内容
        const updatedText = this.editorElement.value;
        
        // 设置字体
        this.ctx!.font = `${textData.fontSize * (1 / creationScale)}px Arial, sans-serif`;
        
        // 重新计算文本宽度和高度
        let newTextWidth = 0;
        const newLines = updatedText.split('\n');
        for (const line of newLines) {
          const metrics = this.ctx!.measureText(line);
          newTextWidth = Math.max(newTextWidth, metrics.width);
        }
        
        // 计算新的总高度
        const newTextHeight = newLines.length * lineHeight - (newLines.length > 1 ? lineHeight * 0.2 : 0);
        
        // 计算新边界框 - 与SelectionService中的getTextBoundingBox一致
        const newHalfWidth = newTextWidth / 2;
        
        // 新的基础边界
        const newBounds = {
          minX: textData.baseX - newHalfWidth,
          minY: textData.baseY - (textData.fontSize * (1 / creationScale) / 2), // 从第一行文字的中心点向上偏移半个字体大小
          maxX: textData.baseX + newHalfWidth,
          maxY: textData.baseY + newTextHeight - (textData.fontSize * (1 / creationScale) / 2) // 向下延伸整个文本高度，减去半个字体大小
        };
        
        // 添加边距
        newBounds.minX -= margin;
        newBounds.minY -= margin;
        newBounds.maxX += margin;
        newBounds.maxY += margin;
        
        // 恢复上下文
        this.ctx!.restore();
        
        // 转换为屏幕坐标
        const newMinPos = this.viewportService.canvasToClient(newBounds.minX, newBounds.minY);
        const newMaxPos = this.viewportService.canvasToClient(newBounds.maxX, newBounds.maxY);
        
        // 更新边框位置和大小
        this.borderElement.style.left = `${newMinPos.x}px`;
        this.borderElement.style.top = `${newMinPos.y}px`;
        this.borderElement.style.width = `${newMaxPos.x - newMinPos.x}px`;
        this.borderElement.style.height = `${newMaxPos.y - newMinPos.y}px`;
      }
    };
    
    // 自动调整大小
    textarea.addEventListener('input', () => {
      this.autoResizeTextarea(textarea, textData);
      // 更新边框大小
      updateBorderSize();
    });
    
    // 失焦时保存并结束编辑
    textarea.addEventListener('blur', () => {
      this.saveEditorContent();
    });
    
    // 特殊键处理
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        textarea.blur();
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        textarea.blur();
      }
    });
    
    // 添加全局点击事件监听器，处理点击文本框外部的情况
    const handleOutsideClick = (e: MouseEvent) => {
      if (!textarea || !textarea.parentNode || !this.borderElement) return;
      
      // 使用边框元素的边界进行检测，确保与视觉上的提示框完全一致
      const borderRect = this.borderElement.getBoundingClientRect();
      
      // 如果点击在边界外，则完成编辑
      if (
        e.clientX < borderRect.left || 
        e.clientX > borderRect.right || 
        e.clientY < borderRect.top || 
        e.clientY > borderRect.bottom
      ) {
        // 移除事件监听器
        document.removeEventListener('mousedown', handleOutsideClick);
        this.outsideClickHandler = null;
        
        // 执行保存和退出编辑
        if (this.isEditing && this.editorElement === textarea) {
          // 设置短延迟，确保其他事件处理完成
          setTimeout(() => {
            textarea.blur();
            
            // 触发一个鼠标移动事件，确保更新提示框状态
            if (this.ctx && this.ctx.canvas) {
              const mouseMoveEvent = new MouseEvent('mousemove', {
                bubbles: true,
                cancelable: true,
                view: window,
                clientX: e.clientX,
                clientY: e.clientY
              });
              this.ctx.canvas.dispatchEvent(mouseMoveEvent);
            }
          }, 10);
        }
      }
    };
    
    // 存储事件处理器引用以便后续移除
    this.outsideClickHandler = handleOutsideClick;
    
    // 延迟添加点击外部事件监听，避免与当前点击事件冲突
    setTimeout(() => {
      document.addEventListener('mousedown', handleOutsideClick);
    }, 100);
    
    // 聚焦但不选择文本
    setTimeout(() => {
      // 先焦点
      textarea.focus();
      
      // 如果提供了点击坐标，尝试放置光标在点击位置
      if (clickX !== undefined && clickY !== undefined) {
        try {
          // 转换为相对于文本框的坐标
          const textareaRect = textarea.getBoundingClientRect();
          const relativeX = clickX - textareaRect.left;
          const relativeY = clickY - textareaRect.top;
          
          // 异步设置光标位置，确保焦点完全获取
          requestAnimationFrame(() => {
            this.setCursorPositionByCoordinates(textarea, relativeX, relativeY);
          });
        } catch (error) {
          // 设置光标位置失败，但不影响主要功能
        }
      }
    }, 10);

    // 添加事件监听器，在缩放或视口变化时更新编辑器位置
    const handleViewportUpdate = () => {
      if (this.isEditing && this.editorElement === textarea) {
        this.updateEditingElementsPosition();
      }
    };

    // 监听视口变化和缩放事件
    document.addEventListener('viewport-changed', handleViewportUpdate);
    
    // 在清理编辑器时移除这个事件监听器
    textarea.addEventListener('blur', () => {
      document.removeEventListener('viewport-changed', handleViewportUpdate);
    }, { once: true });
  }
  
  /**
   * 自动调整文本区域大小
   */
  private autoResizeTextarea(textarea: HTMLTextAreaElement, textData: TextData): void {
    if (!textarea || !this.viewportService || !this.ctx) return;
    
    // 获取当前缩放比例
    const currentScale = this.viewportService.getScale();
    // 获取文本创建时的缩放比例
    const creationScale = textData.creationScale || 1;
    
    // 保存上下文状态
    const ctx = this.ctx;
    ctx.save();
    // 设置字体，与绘制时相同的调整
    ctx.font = `${textData.fontSize * (1 / creationScale)}px Arial, sans-serif`;
    
    // 计算最长行的宽度
    let maxWidth = 0;
    const lines = textarea.value.split('\n');
    for (const line of lines) {
      const metrics = ctx.measureText(line);
      maxWidth = Math.max(maxWidth, metrics.width);
    }
    ctx.restore();
    
    // 将Canvas中的尺寸转换为屏幕上的物理尺寸
    const displayWidth = maxWidth * currentScale;
    const lineHeight = textData.fontSize * (1 / creationScale) * 1.2;
    const textHeight = Math.max(lineHeight, lines.length * lineHeight);
    const displayHeight = textHeight * currentScale;
    
    // 设置文本域的大小，加上少量额外空间
    const extraSpace = 20 * currentScale;
    if (maxWidth > 0) {
      textarea.style.width = `${displayWidth + extraSpace}px`;
      textarea.style.height = `${displayHeight + 10 * currentScale}px`;
    } else {
      // 空文本时使用默认最小宽度
      const minWidth = textData.fontSize * 5 * currentScale;
      textarea.style.width = `${minWidth}px`;
      textarea.style.height = `${lineHeight * currentScale}px`;
    }
    
    // 获取屏幕位置并确保编辑器居中
    const screenPos = this.viewportService.canvasToClient(textData.baseX, textData.baseY);
    textarea.style.left = `${screenPos.x - parseInt(textarea.style.width) / 2}px`;
  }
  
  /**
   * 保存编辑器内容并停止编辑
   */
  private saveEditorContent(): void {
    if (!this.editorElement || this.editingIndex < 0) return;
    
    // 保存文本内容
    const text = this.editorElement.value;
    this.texts[this.editingIndex].text = text;
    
    // 如果文本为空，则删除该文本
    if (text.trim() === '') {
      this.texts.splice(this.editingIndex, 1);
    }
    
    this.stopEditing();
  }
  
  /**
   * 停止编辑
   */
  public stopEditing(): void {
    if (this.editingIndex >= 0 && this.editingIndex < this.texts.length) {
      this.texts[this.editingIndex].isEditing = false;
    }
    
    this.editingIndex = -1;
    this.isEditing = false;
    
    // 移除编辑器
    this.removeEditor();
    
    // 通知应用退出编辑模式
    document.dispatchEvent(new CustomEvent('text-edit-complete'));
    
    // 触发工具操作完成事件，以便自动保存
    document.dispatchEvent(new CustomEvent('tool-operation-complete'));
    
    // 触发一个鼠标移动事件，确保更新提示框状态
    if (this.ctx && this.ctx.canvas) {
      // 使用当前鼠标位置
      const currentMousePos = { x: 0, y: 0 };
      if (typeof MouseEvent === 'function') {
        try {
          const mouseEvent = document.createEvent('MouseEvents');
          mouseEvent.initMouseEvent('mousemove', true, true, window, 0,
            currentMousePos.x, currentMousePos.y, currentMousePos.x, currentMousePos.y,
            false, false, false, false, 0, null);
          this.ctx.canvas.dispatchEvent(mouseEvent);
        } catch (e) {
          // 无法触发鼠标移动事件，但不影响主要功能
        }
      }
    }
    
    // 触发重新渲染
    this.triggerRender();
  }
  
  /**
   * 移除编辑器
   */
  private removeEditor(): void {
    // 移除全局事件监听器
    if (this.outsideClickHandler) {
      document.removeEventListener('mousedown', this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    
    // 移除边框元素
    if (this.borderElement) {
      if (this.borderElement.parentNode) {
        this.borderElement.parentNode.removeChild(this.borderElement);
      }
      this.borderElement = null;
    }
    
    // 移除编辑器元素
    if (this.editorElement) {
      if (this.editorElement.parentNode) {
        this.editorElement.parentNode.removeChild(this.editorElement);
      }
      this.editorElement = null;
    }
  }
  
  /**
   * 删除指定索引的文本
   */
  public deleteTexts(indexes: number[]): void {
    if (!indexes.length) return;
    
    // 如果当前正在编辑的文本将被删除，停止编辑
    if (this.editingIndex !== -1 && indexes.includes(this.editingIndex)) {
      this.stopEditing();
    }
    
    // 从大到小排序并删除
    const sortedIndexes = [...indexes].sort((a, b) => b - a);
    sortedIndexes.forEach(index => {
      if (index >= 0 && index < this.texts.length) {
        this.texts.splice(index, 1);
      }
    });
    
    this.triggerRender();
  }
  
  /**
   * 绘制所有文本
   */
  public drawAllTexts(): void {
    if (!this.ctx || !this.viewportService) return;
    
    this.texts.forEach((text, index) => {
      // 如果文本内容为空且不在编辑中，不绘制
      if (text.text.length === 0 && !text.isEditing) return;
      
      // 如果正在编辑，不在canvas上绘制
      if (index === this.editingIndex && this.isEditing) return;
      
      // 获取创建时的缩放比例，如果没有则使用默认值1
      const creationScale = text.creationScale || 1;
      
      // 设置字体大小和颜色，字体大小考虑创建时的缩放比例
      // 缩放比例越小（视野越广），文本物理大小越大
      this.ctx!.font = `${text.fontSize * (1 / creationScale)}px Arial, sans-serif`;
      this.ctx!.fillStyle = text.color;
      
      // 绘制多行文本
      const lines = text.text.split('\n');
      const lineHeight = text.fontSize * (1 / creationScale) * 1.2;
      
      lines.forEach((line, i) => {
        const metrics = this.ctx!.measureText(line); // 测量文本宽度
        const x = text.baseX - metrics.width / 2; // 计算文本中心点
        const y = text.baseY + lineHeight/2 - 3 * (1 / creationScale) + (i * lineHeight); // 计算文本行位置
        this.ctx!.fillText(line, x, y); // 绘制文本
      });
    });
  }
  
  /**
   * 检测点击位置是否命中文本
   */
  public hitTest(point: Point): number {
    if (!this.ctx || !this.viewportService) return -1;
    
    // 从前往后检测，使渲染顺序与点击检测一致
    for (let i = 0; i < this.texts.length; i++) {
      const text = this.texts[i];
      
      // 获取创建时的缩放比例，如果没有则使用默认值1
      const creationScale = text.creationScale || 1;
      
      // 设置字体，与绘制时相同的调整
      this.ctx.font = `${text.fontSize * (1 / creationScale)}px Arial, sans-serif`;
      
      // 计算文本的宽度和高度
      let maxWidth = 0;
      const lines = text.text.split('\n');
      for (const line of lines) {
        const metrics = this.ctx.measureText(line);
        maxWidth = Math.max(maxWidth, metrics.width);
      }
      
      // 空文本使用默认宽度
      if (maxWidth === 0) {
        maxWidth = text.fontSize * (1 / creationScale);
      }
      
      // 计算整个文本块的中心点
      const halfWidth = maxWidth / 2;
      
      // 计算行高和总高度
      const lineHeight = text.fontSize * (1 / creationScale) * 1.2;
      const textHeight = lines.length * lineHeight - (lines.length > 1 ? lineHeight * 0.2 : 0);
      
      // 基础边界（与SelectionService.getTextBoundingBox保持一致）
      const bounds = {
        minX: text.baseX - halfWidth,
        minY: text.baseY - (text.fontSize * (1 / creationScale) / 2), // 从第一行文字的中心点向上偏移半个字体大小
        maxX: text.baseX + halfWidth,
        maxY: text.baseY + textHeight - (text.fontSize * (1 / creationScale) / 2) // 向下延伸整个文本高度，减去半个字体大小
      };
      
      // 添加边距，保持与边框显示一致（5像素）
      const padding = 5 * (1 / creationScale);
      bounds.minX -= padding;
      bounds.minY -= padding;
      bounds.maxX += padding;
      bounds.maxY += padding;
      
      // 应用额外的偏移量
      if (text.offsetX || text.offsetY) {
        const offsetX = (text.offsetX || 0) / creationScale;
        const offsetY = (text.offsetY || 0) / creationScale;
        
        bounds.minX += offsetX;
        bounds.minY += offsetY;
        bounds.maxX += offsetX;
        bounds.maxY += offsetY;
      }
      
      // 检查点击是否在边界框内
      if (point.x >= bounds.minX && point.x <= bounds.maxX &&
          point.y >= bounds.minY && point.y <= bounds.maxY) {
        return i;
      }
    }
    
    return -1;
  }
  
  /**
   * 触发画布重新渲染
   */
  private triggerRender(): void {
    if (this.viewportService) {
      const event = new CustomEvent('render-required');
      document.dispatchEvent(event);
    }
  }
  
  /**
   * 清理资源
   */
  public cleanup(): void {
    // 移除全局事件监听器
    if (this.outsideClickHandler) {
      document.removeEventListener('mousedown', this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    
    this.removeEditor();
  }
  
  /**
   * 根据坐标估算光标位置
   */
  private setCursorPositionByCoordinates(textarea: HTMLTextAreaElement, x: number, y: number): void {
    // 获取文本和样式信息
    const text = textarea.value;
    const computedStyle = getComputedStyle(textarea);
    const fontSize = parseFloat(computedStyle.fontSize);
    const lineHeight = parseFloat(computedStyle.lineHeight) || fontSize * 1.2;
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingLeft = parseFloat(computedStyle.paddingLeft);
    
    // 获取文本框的尺寸
    const textareaWidth = textarea.clientWidth;
    
    // 调整坐标，考虑内边距
    const adjustedX = Math.max(0, x - paddingLeft);
    const adjustedY = Math.max(0, y - paddingTop);
    
    // 分割成行
    const lines = text.split('\n');
    
    // 计算点击的行号
    const clickedLineIndex = Math.min(Math.floor(adjustedY / lineHeight), lines.length - 1);
    const clickedLine = lines[clickedLineIndex] || '';
    
    // 使用临时元素测量文本宽度
    const tempSpan = document.createElement('span');
    tempSpan.style.font = computedStyle.font;
    tempSpan.style.fontSize = computedStyle.fontSize;
    tempSpan.style.letterSpacing = computedStyle.letterSpacing;
    tempSpan.style.whiteSpace = 'pre';
    tempSpan.style.position = 'absolute';
    tempSpan.style.visibility = 'hidden';
    document.body.appendChild(tempSpan);
    
    // 根据文本居中对齐计算偏移
    tempSpan.textContent = clickedLine;
    const lineWidth = tempSpan.getBoundingClientRect().width;
    const centerOffset = (textareaWidth - lineWidth) / 2;
    
    // 调整点击的X坐标，考虑文本居中对齐
    const centerAdjustedX = adjustedX - centerOffset;
    
    // 二分查找找到最接近的字符位置
    let charIndex = this.findClosestCharIndex(clickedLine, centerAdjustedX, tempSpan);
    
    // 清理临时元素
    document.body.removeChild(tempSpan);
    
    // 计算总偏移量
    let offset = 0;
    for (let i = 0; i < clickedLineIndex; i++) {
      offset += lines[i].length + 1; // +1 是换行符
    }
    offset += charIndex;
    
    // 确保不超出文本范围
    offset = Math.min(offset, text.length);
    
    // 设置光标位置
    textarea.setSelectionRange(offset, offset);
  }
  
  /**
   * 使用二分查找找到最接近点击位置的字符索引
   */
  private findClosestCharIndex(line: string, targetX: number, measureElement: HTMLElement): number {
    if (line.length === 0) return 0;
    
    // 若点击位置在左侧负区域，直接返回0
    if (targetX <= 0) {
      return 0;
    }
    
    // 先检查全部文本宽度，若小于点击位置，直接返回行尾
    measureElement.textContent = line;
    const fullWidth = measureElement.getBoundingClientRect().width;
    if (fullWidth <= targetX) {
      return line.length;
    }
    
    let low = 0;
    let high = line.length;
    
    // 二分查找
    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      measureElement.textContent = line.substring(0, mid);
      const width = measureElement.getBoundingClientRect().width;
      
      if (width < targetX) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }
    
    // 校准结果：比较low-1和low哪个更接近
    if (low > 0) {
      measureElement.textContent = line.substring(0, low - 1);
      const beforeWidth = measureElement.getBoundingClientRect().width;
      
      measureElement.textContent = line.substring(0, low);
      const afterWidth = measureElement.getBoundingClientRect().width;
      
      // 选择距离更近的字符位置
      if (Math.abs(targetX - beforeWidth) < Math.abs(targetX - afterWidth)) {
        return low - 1;
      }
    }
    
    return low;
  }
  
  /**
   * 更新编辑元素的位置
   */
  public updateEditingElementsPosition(): void {
    if (!this.editorElement || !this.viewportService || this.editingIndex < 0 || !this.ctx) return;
    
    const textData = this.texts[this.editingIndex];
    if (!textData) return;
    
    // 获取当前缩放比例
    const currentScale = this.viewportService.getScale();
    // 获取文本创建时的缩放比例
    const creationScale = textData.creationScale || 1;
    // 计算相对缩放比例（创建时相对于当前的比例）
    const relativeScale = creationScale / currentScale;
    
    // 转换canvas坐标到屏幕坐标
    const screenPos = this.viewportService.canvasToClient(textData.baseX, textData.baseY);
    
    // 使用考虑缩放比例后的字体大小，并更新文本框的字体大小
    const displayFontSize = textData.fontSize / relativeScale;
    this.editorElement.style.fontSize = `${displayFontSize}px`;
    
    // 保存当前上下文状态
    this.ctx.save();
    
    // 设置字体大小，与drawAllTexts方法中的设置一致
    this.ctx.font = `${textData.fontSize * (1 / creationScale)}px Arial, sans-serif`;
    
    // 计算文本显示尺寸
    let maxWidth = 0;
    const lines = this.editorElement.value.split('\n');
    for (const line of lines) {
      const metrics = this.ctx.measureText(line);
      maxWidth = Math.max(maxWidth, metrics.width);
    }
    this.ctx.restore();
    
    // 将Canvas中的文本尺寸转换为屏幕上的物理尺寸
    const displayWidth = maxWidth * currentScale;
    const lineHeight = textData.fontSize * (1 / creationScale) * 1.2;
    const textHeight = Math.max(lineHeight, lines.length * lineHeight);
    const displayHeight = textHeight * currentScale;
    
    // 设置文本域的大小，加上少量额外空间
    const extraSpace = 20 * currentScale;
    if (maxWidth > 0) {
      this.editorElement.style.width = `${displayWidth + extraSpace}px`;
      this.editorElement.style.height = `${displayHeight + 10 * currentScale}px`;
    } else {
      // 空文本时使用默认最小宽度
      const minWidth = textData.fontSize * 5 * currentScale;
      this.editorElement.style.width = `${minWidth}px`;
      this.editorElement.style.height = `${lineHeight * currentScale}px`;
    }
    
    // 更新编辑器位置，确保居中
    this.editorElement.style.left = `${screenPos.x - parseInt(this.editorElement.style.width) / 2}px`;
    this.editorElement.style.top = `${screenPos.y - (displayFontSize)/2}px`;
    
    // 更新边框位置
    if (this.borderElement) {
      // 保存上下文
      this.ctx.save();
      
      // 设置字体，确保与绘制和SelectionService一致
      this.ctx.font = `${textData.fontSize * (1 / creationScale)}px Arial, sans-serif`;
      
      // 使用textarea的实际内容来计算文本宽度和高度
      const currentText = this.editorElement.value;
      
      // 计算文本的宽度和高度
      let textWidth = 0;
      const newLines = currentText.split('\n');
      for (const line of newLines) {
        const metrics = this.ctx.measureText(line);
        textWidth = Math.max(textWidth, metrics.width);
      }
      
      // 计算总高度
      const newTextHeight = newLines.length * lineHeight - (newLines.length > 1 ? lineHeight * 0.2 : 0);
      
      // 计算边界框 - 与SelectionService中的getTextBoundingBox一致
      const halfWidth = textWidth / 2;
      
      // 基础边界（不包含边距）
      const newBounds = {
        minX: textData.baseX - halfWidth,
        minY: textData.baseY - (textData.fontSize * (1 / creationScale) / 2),
        maxX: textData.baseX + halfWidth,
        maxY: textData.baseY + newTextHeight - (textData.fontSize * (1 / creationScale) / 2)
      };
      
      // 添加边距
      const margin = 5 / currentScale;
      newBounds.minX -= margin;
      newBounds.minY -= margin;
      newBounds.maxX += margin;
      newBounds.maxY += margin;
      
      // 恢复上下文
      this.ctx.restore();
      
      // 转换为屏幕坐标
      const newMinPos = this.viewportService.canvasToClient(newBounds.minX, newBounds.minY);
      const newMaxPos = this.viewportService.canvasToClient(newBounds.maxX, newBounds.maxY);
      
      // 更新边框位置和大小
      this.borderElement.style.left = `${newMinPos.x}px`;
      this.borderElement.style.top = `${newMinPos.y}px`;
      this.borderElement.style.width = `${newMaxPos.x - newMinPos.x}px`;
      this.borderElement.style.height = `${newMaxPos.y - newMinPos.y}px`;
    }
  }

  /**
   * 设置文本光标位置
   */
  public setCursorPosition(text: HTMLElement, position: number): void {
    try {
      const range = document.createRange();
      const sel = window.getSelection();
      
      // 如果文本内容为空，直接设置为空范围
      if (text.childNodes.length === 0) {
        range.setStart(text, 0);
        range.collapse(true);
        sel?.removeAllRanges();
        sel?.addRange(range);
        return;
      }
      
      // 如果第一个子节点是文本节点
      if (text.firstChild && text.firstChild.nodeType === Node.TEXT_NODE) {
        const textNode = text.firstChild;
        const textContent = textNode.textContent || '';
        
        // 确保位置在有效范围内
        position = Math.max(0, Math.min(position, textContent.length));
        
        range.setStart(textNode, position);
        range.collapse(true);
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    } catch (error) {
      // 设置光标位置失败，但不影响主要功能
    }
  }

  /**
   * 触发鼠标移动事件
   */
  private triggerMouseMove(element: HTMLElement): void {
    try {
      // 获取元素的当前位置
      const rect = element.getBoundingClientRect();
      
      // 创建并分发鼠标移动事件
      const event = new MouseEvent('mousemove', {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2
      });
      
      // 分发事件
      element.dispatchEvent(event);
    } catch (e) {
      // 无法触发鼠标移动事件，但不影响主要功能
    }
  }
}


